import {addBreadcrumb, captureMessage} from "@sentry/react";

/**
 * Debug PV-1789: traque où un quoteLineInfoProductAttribute perd sa clé `productAttribute`
 * (shape attendue: {productAttribute: {id, type, ...}, label, value} ; shape corrompue: {value}).
 *
 * Produit un résumé compact (safe pour Sentry) de la shape des attributs par quoteLine.
 */
export function summarizeQuoteLinesAttributes(quoteLines) {
    try {
        if (!Array.isArray(quoteLines)) return {error: 'not-an-array', typeof: typeof quoteLines};

        return quoteLines.map((line, i) => {
            const attrs = line?.quoteLineInfo?.quoteLineInfoProductAttributes;
            if (!Array.isArray(attrs)) {
                return {i, attrs: attrs === undefined ? 'undefined' : typeof attrs};
            }

            let missing = 0;
            const paTypeofSet = new Set();
            let firstMissing = null;
            attrs.forEach((a, j) => {
                const pa = a?.productAttribute;
                paTypeofSet.add(pa === undefined ? 'undefined' : (pa === null ? 'null' : typeof pa));
                if (pa == null || (typeof pa === 'object' && pa.type == null)) {
                    missing += 1;
                    if (firstMissing === null) {
                        firstMissing = {j, keys: Object.keys(a ?? {}), value: a?.value};
                    }
                }
            });

            return {
                i,
                count: attrs.length,
                missing,                                   // nb d'attributs sans productAttribute exploitable
                paTypeof: Array.from(paTypeofSet),         // ['object'] sain ; contient 'undefined'/'null'/'string' = corrompu
                firstMissing,                              // 1er attribut fautif (clés réelles + value)
            };
        });
    } catch (e) {
        return {error: String(e?.message ?? e)};
    }
}

/** True si au moins une quoteLine a un attribut sans productAttribute exploitable. */
export function hasStrippedAttributes(summary) {
    return Array.isArray(summary) && summary.some(l => (l?.missing ?? 0) > 0);
}

/**
 * Pose un breadcrumb + (si strip détecté) un captureMessage taggé, pour situer dans le cycle
 * QUI corrompt les attributs (load / recalc replace / save).
 */
export function trackQuoteAttributes(stage, quoteLines, context = {}) {
    try {
        const summary = summarizeQuoteLinesAttributes(quoteLines);
        const stripped = hasStrippedAttributes(summary);

        addBreadcrumb({
            category: 'pv-1789',
            level: stripped ? 'warning' : 'info',
            message: `attrs@${stage}${stripped ? ' STRIPPED' : ''}`,
            data: {stage, stripped, summary, ...context},
        });

        if (stripped) {
            captureMessage(`PV-1789 attributs strippés détectés @${stage}`, {
                level: 'warning',
                tags: {issueLink: 'https://sfimultimedia.atlassian.net/browse/PV-1789', pv1789Stage: stage},
                extra: {stage, summary, ...context},
            });
        }

        return {summary, stripped};
    } catch (e) {
        return {summary: {error: String(e?.message ?? e)}, stripped: false};
    }
}
